
import { Box, useTheme } from "@mui/material";
import { ApexOptions } from "apexcharts";
import { useMUIThemeModeContext } from "context/MUIThemeModeContext";
import { useUserDataContext } from "context/UserDataContext";
import dayjs from "dayjs";
import { useAPIDashboardStrategiesCharts } from "hooks/useAPI";
import useExchangeRate from "hooks/useExchangeRate";
import useExchangesMetadataGlobal from "hooks/useExchangesMetadataGlobal";
import ReactApexChart from "react-apexcharts";
import { useTranslation } from "react-i18next";
import { IPriceChart } from "types/types";
import { percentageChange, splitCurrencyPair } from "utils";
import { renderNumber, renderPrice } from "utils/formatter";

import { SMALL_CHART_MAX_SAMPLES, SUMMARY_CHART_HEIGHT } from "..";

interface Props {
  chartData: IPriceChart;
  currencyPair: string;
  isFullScreen?: boolean;
}

const PriceChart = ({ chartData, currencyPair, isFullScreen }: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { summariesSettings, showInUSD } = useUserDataContext();
  const { getUSDPrice } = useExchangeRate();
  const { colors } = useMUIThemeModeContext();
  const { counterCurrDisplayedScale, counterCurrShortDisplayedScale } = useExchangesMetadataGlobal(currencyPair);
  const { counterCurrency } = splitCurrencyPair(currencyPair);

  const shouldFetchFullscreen = !!isFullScreen && chartData.points.length >= SMALL_CHART_MAX_SAMPLES;
  const { data: fullscreenData } = useAPIDashboardStrategiesCharts(shouldFetchFullscreen, { ...summariesSettings, fullscreen: true });

  const effectiveChartData: IPriceChart
    = shouldFetchFullscreen && fullscreenData?.chartsByCurrencyPair[currencyPair]?.priceChart
      ? fullscreenData.chartsByCurrencyPair[currencyPair].priceChart
      : chartData;

  const labels = effectiveChartData.points.map((item) =>
    Intl.DateTimeFormat("cs-CZ").format(new Date(dayjs(item.d).format()))
  );

  const series = [
    {
      name: t("price"),
      data: effectiveChartData.points.map((item) => item.p),
    },
    {
      name: t("averagePrice"),
      data: effectiveChartData.points.map((item) => item.a),
    },
  ];

  const options: ApexOptions = {
    theme: { mode: theme.palette.mode },
    tooltip: {
      followCursor: true,
      inverseOrder: true,
      y: {
        formatter: (val: number, { seriesIndex, dataPointIndex, series }) => {
          // not possible to have a separate formatter for each data series, therefore this hack
          if (seriesIndex === 0) {
            const percentage = percentageChange(series?.[1]?.[dataPointIndex], val);

            const price = showInUSD
              ? getUSDPrice(counterCurrency, val, counterCurrDisplayedScale)
              : renderPrice(val, counterCurrency, counterCurrDisplayedScale);
            const result = `${price} (${percentage > 0 ? "+" : ""}${renderNumber(percentage, 2)} %)`;
            return result;
          } else {
            const value = showInUSD ? getUSDPrice(counterCurrency, val, counterCurrDisplayedScale) : renderPrice(val, counterCurrency, counterCurrDisplayedScale);
            return value ?? "";
          }
        },
      },
    },
    colors: [theme.palette.primary.main, theme.palette.success.main],
    chart: {
      type: "line",
      zoom: { enabled: false },
      toolbar: { show: false },
      background: "transparent",
    },
    dataLabels: { enabled: false },
    stroke: {
      curve: ["smooth", "stepline"] as ApexStroke["curve"],
      width: [3, 3],
    },
    grid: {
      row: {
        colors: [colors.legacyGray100, "transparent"],
        opacity: 0.5,
      },
    },
    xaxis: {
      categories: labels,
      labels: {
        hideOverlappingLabels: true,
        rotate: -30,
        show: true,
      },
      tooltip: { enabled: false },
    },
    yaxis: {
      show: true,
      min: effectiveChartData.minValue,
      labels: {
        formatter: (val: any) => {
          const value = showInUSD ? getUSDPrice(counterCurrency, val, counterCurrShortDisplayedScale) : renderPrice(val, counterCurrency, counterCurrShortDisplayedScale);
          return value ?? "";
        },
      },
    },
  };

  return (
    <Box sx={{ pr: { xs: 0, md: 2 }, textAlign: "center", height: "100%" }}>
      <ReactApexChart
        options={options}
        series={series}
        type="line"
        width={"100%"}
        height={isFullScreen ? "95%" : SUMMARY_CHART_HEIGHT}
      />
    </Box>
  );
};

export default PriceChart;
